import { useEffect, useState } from "react";

/**
 * Persisted UI state for the Dashboard "chrome" (left nav rail + right summary panel).
 * Keys are namespaced so they don't collide with existing dashboard preferences.
 */

const NAV_RAIL_KEY = "pk.dash.navRail.collapsed";
const SUMMARY_KEY = "pk.dash.summaryPanel.collapsed";
// Legacy keys from earlier iterations; migrated then removed.
const LEGACY_SUMMARY_KEY = "dashboard-summary-collapsed";
const LEGACY_RIGHT_RAIL_HIDDEN_KEY = "pk.dash.summaryPanel.hidden";

function readBool(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  const v = localStorage.getItem(key);
  if (v === null) return fallback;
  return v === "true";
}

export function useDashboardChromeState() {
  // Default: rail expanded on wide screens, collapsed otherwise.
  const [navRailCollapsed, setNavRailCollapsed] = useState<boolean>(() => {
    const fallback =
      typeof window !== "undefined" ? window.innerWidth < 1280 : false;
    return readBool(NAV_RAIL_KEY, fallback);
  });

  // Default: summary panel closed (opened on demand via header bell).
  const [summaryCollapsed, setSummaryCollapsed] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const legacy = localStorage.getItem(LEGACY_SUMMARY_KEY);
      if (localStorage.getItem(SUMMARY_KEY) === null && legacy !== null) {
        localStorage.removeItem(LEGACY_SUMMARY_KEY);
        return legacy === "true";
      }
      // One-shot cleanup of the deprecated right-rail-hidden key.
      localStorage.removeItem(LEGACY_RIGHT_RAIL_HIDDEN_KEY);
    }
    return readBool(SUMMARY_KEY, true);
  });

  useEffect(() => {
    localStorage.setItem(NAV_RAIL_KEY, String(navRailCollapsed));
  }, [navRailCollapsed]);

  useEffect(() => {
    localStorage.setItem(SUMMARY_KEY, String(summaryCollapsed));
  }, [summaryCollapsed]);

  return {
    navRailCollapsed,
    setNavRailCollapsed,
    toggleNavRail: () => setNavRailCollapsed((v) => !v),
    summaryCollapsed,
    setSummaryCollapsed,
    toggleSummary: () => setSummaryCollapsed((v) => !v),
  };
}
